// PO-GST-01 · P2-D1-D · amendment request mapping only (B2 validation stays on the backend).
import { editLineGst, gstHeaderDiff, gstHeaderFromPo } from "./poGst";

const GST_HDR_KEYS = ["place_of_supply_state_code", "company_is_receiving_location", "reverse_charge", "charges"];

// Effective proposal baseline = live PO overlaid with the saved Draft pending_changes.
export function amendBaseline(po, pending) {
  const ph = pending?.header || {};
  const header = { ...po };
  ["expected_date", "remarks", ...GST_HDR_KEYS].forEach((k) => { if (ph[k] !== undefined) header[k] = ph[k]; });
  const pl = Object.fromEntries((pending?.lines || []).map((l) => [l.line_no, l]));
  const lines = Object.fromEntries((po?.lines || []).map((l) => [l.line_no, { ...l, ...(pl[l.line_no] || {}) }]));
  return { header, lines };
}

export function initAmendGst(po, pending) {
  const base = amendBaseline(po, pending);
  const lines = {};
  Object.values(base.lines).forEach((l) => {
    lines[l.line_no] = {
      hsn_code: l.hsn_code || "", tax_pct: l.tax_pct ?? "", zero_confirmed: l.tax_pct === 0 && !!l.gst_rate_source,
      gst_rate_source: l.gst_rate_source || "", discount_pct: l.discount_pct ?? "", discount_amt: l.discount_amt ?? "",
      expected_date: (l.expected_date || "").slice(0, 10), gst_dirty: false,
    };
  });
  return { header: gstHeaderFromPo(base.header), lines, expectedDate: (base.header.expected_date || "").slice(0, 10) };
}

// PO-AMEND-REV-01 · qty / rate / notes are compared against the saved proposal (live PO overlaid with
// pending), so restoring the authorised value is sent explicitly and replaces the old proposal value.
function legacyLinePatch(l, edit) {
  const patch = {};
  if (edit.qty_adjustment !== undefined) {
    const raw = edit.qty_adjustment;
    const proposed = (raw === "" || raw === null) ? 0 : Number(raw);
    if (!Number.isNaN(proposed) && proposed !== Number(l.qty_adjustment ?? 0)) patch.qty_adjustment = proposed;
  }
  if (edit.adjustment_notes !== undefined && edit.adjustment_notes !== (l.adjustment_notes || "")) {
    patch.adjustment_notes = edit.adjustment_notes;
  }
  if (edit.unit_cost !== undefined && edit.unit_cost !== "" && Number(edit.unit_cost) !== Number(l.unit_cost ?? 0)) {
    patch.unit_cost = Number(edit.unit_cost);
  }
  return patch;
}

export function buildAmendBody({ po, pending, reason, expectedDate, remarks, lineEdits, gstPo, gstHdr, gstLines }) {
  const base = amendBaseline(po, pending);
  const lines = (po.lines || []).map((l) => {
    const patch = { line_no: l.line_no, ...legacyLinePatch(base.lines[l.line_no] || l, lineEdits[l.line_no] || {}) };
    if (gstPo && gstLines?.[l.line_no]) {
      const gl = gstLines[l.line_no];
      const orig = base.lines[l.line_no] || {};
      Object.assign(patch, editLineGst(gl, orig));
      if ((gl.expected_date || "") !== (orig.expected_date || "").slice(0, 10)) patch.expected_date = gl.expected_date || "";
    }
    return Object.keys(patch).length > 1 ? patch : null;
  }).filter(Boolean);
  const body = { amendment_reason: (reason || "").trim() };
  // D3 · sent whenever it differs from the saved proposal / live value, including an explicit clear.
  if ((expectedDate || "") !== (base.header.expected_date || "").slice(0, 10)) body.expected_date = expectedDate || "";
  if ((remarks || "") !== (base.header.remarks || "")) body.remarks = remarks || "";
  if (lines.length > 0) body.lines = lines;
  if (gstPo && gstHdr) Object.assign(body, gstHeaderDiff(gstHdr, base.header));
  return body;
}

const fmtTs = (v) => (v || "").slice(0, 19).replace("T", " ");

// Stored amendment calculations → GstResultPanel shape (labels make the source explicit).
export function storedAmendResult(g, kind) {
  if (!g) return null;
  const label = kind === "submitted"
    ? `Submitted for re-authorisation · proposed Rev ${g.proposed_revision} · ${fmtTs(g.submitted_at)}`
    : `Saved Draft calculation · proposed Rev ${g.proposed_revision} · calculated ${fmtTs(g.computed_at)}`;
  return { kind, label, tax_status: g.tax_status, totals: g.totals, classification: g.classification,
    findings: g.findings || [], blockers: (g.delivery_findings || []).filter((f) => f.severity === "BLOCKING") };
}

export function amendPreviewResult(p) {
  if (!p || p.gst_controlled === false) return null;
  const seen = new Set((p.findings || []).map((f) => `${f.code}|${f.line_no ?? ""}`));
  return { kind: "preview", label: `Unsaved preview · not committed · proposed Rev ${p.proposed_revision}`,
    tax_status: p.tax_status, totals: p.totals, classification: p.classification, findings: p.findings || [],
    blockers: (p.submit_blockers || []).filter((f) => !seen.has(`${f.code}|${f.line_no ?? ""}`)) };
}

export function currentEffectiveResult(po) {
  const e = po?.tax_snapshot;
  if (!e?.snapshot) return null;
  return { kind: "current", label: `Current effective · Rev ${e.po_revision} · Snapshot #${e.seq} · ${fmtTs(e.saved_at)}`,
    tax_status: e.snapshot.tax_status, totals: e.snapshot.totals, classification: e.snapshot.classification,
    findings: e.snapshot.findings || [], blockers: [] };
}

export const CANCEL_REASON_MIN = 4;
export const cancelReasonValid = (r) => (r || "").trim().length >= CANCEL_REASON_MIN;
